import type { ReactNode } from 'react'

import type { Severity } from '../../types/api'
import { PILL_DOT, PILL_EDGE, SEVERITY_LABEL, type PillTone } from './pillTones'

export type { PillTone } from './pillTones'

/**
 * The one pill style (DEC-193 decision 2, both themes): a card-coloured pill, a 1px
 * edge in the hue, a bright dot and ink text at weight 500. Before DEC-193 the app had
 * solid fills with bold dark text (DEC-183), soft chips and tinted status pills side by
 * side; the founder picked style B, "outline and dot", on the round 11b mock.
 *
 * Colour is never the only signal: the word is always in the pill. The edge is
 * decorative, the dot is a mark (3:1, `mark-*`) and the text is ink, so every pill reads
 * the same in every list, on every surface.
 *
 * Every pill in the app is built from here: severity (`SeverityBadge`), scan status
 * (`scans/ScanRow` StatusPill), the Debt trend and Executive pills (`trends/Pill`), the
 * Checked tab verdicts and the Monitor's agent rows.
 */
export function PillDot({ tone, size = 8, pulse = false }: { tone: PillTone; size?: number; pulse?: boolean }) {
  return (
    <span
      aria-hidden
      className={`inline-block flex-none rounded-full ${PILL_DOT[tone]} ${pulse ? 'animate-pulse' : ''}`}
      style={{ width: size, height: size }}
    />
  )
}

interface TonePillProps {
  tone: PillTone
  children: ReactNode
  /** Neutral pills (an hours figure, "Still open") carry no dot, as in the mock. */
  dot?: boolean
  pulse?: boolean
  testId?: string
}

export function TonePill({ tone, children, dot = tone !== 'neutral', pulse = false, testId }: TonePillProps) {
  return (
    <span
      data-testid={testId}
      data-tone={tone}
      className={`inline-flex w-fit items-center gap-[6px] whitespace-nowrap rounded-full border bg-panel py-[2px] ${
        dot ? 'pl-2 pr-[10px]' : 'px-[10px]'
      } text-[12px] font-medium leading-4 tabular-nums text-ink ${PILL_EDGE[tone]}`}
    >
      {dot && <PillDot tone={tone} pulse={pulse} />}
      {children}
    </span>
  )
}

export function SeverityBadge({ severity, children }: { severity: Severity; children?: ReactNode }) {
  return <TonePill tone={severity}>{children ?? SEVERITY_LABEL[severity]}</TonePill>
}

interface CategoryBadgeProps {
  category: string
}

/** A category tag: not a status, so no dot and no hue (the mock's "tag"). */
export function CategoryBadge({ category }: CategoryBadgeProps) {
  return (
    <span className="inline-flex w-fit items-center whitespace-nowrap rounded-full bg-fill-subtle px-2 py-[2px] text-[11px] font-semibold uppercase tracking-[1px] text-body">
      {category}
    </span>
  )
}
